/**
 * ThemeSelector renders a button to toggle between light and dark themes.
 *
 * Why: Users need a way to switch themes, but some games only support one theme.
 * This component handles that logic and shows the appropriate icon (sun/moon)
 * based on the current theme.
 *
 * When to use: Rendered by Header in the right-side button group.
 * Disabled when the active game only supports one theme.
 */
import * as React from 'react'
import { useTheme } from '../../../contexts/ThemeContext'
import { cn } from '@merels/ui'
import { IconButton } from '../../library/IconButton/IconButton'
import { SunIcon } from '../../library/Icons/SunIcon'
import { MoonIcon } from '../../library/Icons/MoonIcon'

export interface ThemeSelectorProps {
  buttonRef?: React.Ref<HTMLButtonElement>
  tabIndex?: number
  isFocused?: boolean
  /** Override app accent (e.g. active turn color). */
  accentColor?: string
  accentInkColor?: string
  backgroundColor?: string
}

export function ThemeSelector({ buttonRef, tabIndex, isFocused, accentColor: accentColorProp, accentInkColor = '#ffffff', backgroundColor: backgroundColorProp }: ThemeSelectorProps = {}) {
  const { setUserTheme, effectiveTheme, gameThemeInput, getAppColor } = useTheme()
  const appColorShades = getAppColor()
  const accentColor = accentColorProp ?? appColorShades.main
  const backgroundColor = backgroundColorProp ?? appColorShades.background

  // Check if game only supports one theme
  const gameSupportsOnlyOne = gameThemeInput && gameThemeInput.supportedThemes && gameThemeInput.supportedThemes.length === 1
  const gameTheme = gameSupportsOnlyOne && gameThemeInput.supportedThemes ? gameThemeInput.supportedThemes[0] : null

  // Determine if theme can be switched
  const canSwitch = !gameSupportsOnlyOne

  const handleToggle = () => {
    if (canSwitch) {
      // Toggle to the opposite theme
      setUserTheme(effectiveTheme === 'dark' ? 'light' : 'dark')
    }
  }

  // Show sun icon when in dark mode (to switch to light)
  // Show moon icon when in light mode (to switch to dark)
  const isDarkMode = effectiveTheme === 'dark'
  const targetTheme = isDarkMode ? 'light' : 'dark'

  return (
    <IconButton
      ref={buttonRef}
      onClick={handleToggle}
      disabled={!canSwitch}
      className={cn(
        "flex h-[var(--menu-btn-size,36px)] w-[var(--menu-btn-size,36px)] items-center justify-center rounded-lg transition-colors",
        !canSwitch && "opacity-50 cursor-not-allowed",
        isFocused && "outline-none"
      )}
      style={{
        ...(canSwitch && accentColor ? { color: accentColor } : {}),
        ...(isFocused && accentColor ? {
          boxShadow: `0 0 0 2px ${accentColor}, 0 0 0 4px ${backgroundColor}`,
        } : {}),
      }}
      tabIndex={tabIndex}
      aria-label={`Switch to ${targetTheme} theme`}
      title={canSwitch ? `Switch to ${targetTheme} theme` : `Game only supports ${gameTheme} theme`}
      onMouseEnter={(e) => {
        if (canSwitch && accentColor) {
          e.currentTarget.style.backgroundColor = accentColor
          e.currentTarget.style.color = accentInkColor
        }
      }}
      onMouseLeave={(e) => {
        if (canSwitch && accentColor) {
          e.currentTarget.style.backgroundColor = 'transparent'
          e.currentTarget.style.color = accentColor
        }
      }}
      icon={
        isDarkMode ? (
          <SunIcon width="var(--menu-icon-size,20px)" height="var(--menu-icon-size,20px)" />
        ) : (
          <MoonIcon width="var(--menu-icon-size,20px)" height="var(--menu-icon-size,20px)" />
        )
      }
    />
  )
}

